import '../styles/main.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'swiper/css';
import 'swiper/css/navigation';

import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import { fallbackInterviews, type InterviewEntry, type InterviewQA } from './modules/interview-data';

declare global {
  interface Window {
    withGrowthData?: {
      interviews?: InterviewEntry[];
    };
  }
}

const getInterviewData = (): InterviewEntry[] => {
  const payload = window.withGrowthData?.interviews;
  if (Array.isArray(payload) && payload.length > 0) {
    return payload;
  }
  return fallbackInterviews;
};

const setupMobileMenu = () => {
  const toggleButton = document.querySelector<HTMLButtonElement>('[data-mobile-toggle]');
  const mobileMenu = document.getElementById('mobile-menu');
  const menuLinks = document.querySelectorAll<HTMLAnchorElement>('.mobile-menu-link');

  if (!toggleButton || !mobileMenu) {
    return;
  }

  const toggleMenu = () => {
    const isOpen = !mobileMenu.classList.contains('-translate-x-full');
    mobileMenu.classList.toggle('-translate-x-full');
    document.documentElement.classList.toggle('overflow-hidden', !isOpen);
    toggleButton.setAttribute('aria-expanded', (!isOpen).toString());
    toggleButton.innerHTML = !isOpen
      ? '<i class="fas fa-times fa-2x"></i>'
      : '<i class="fas fa-bars fa-2x"></i>';
  };

  toggleButton.addEventListener('click', toggleMenu);
  menuLinks.forEach((link) => {
    link.addEventListener('click', () => {
      if (!mobileMenu.classList.contains('-translate-x-full')) {
        toggleMenu();
      }
    });
  });
};

const setupHeaderState = () => {
  const header = document.querySelector<HTMLElement>('[data-site-header]');
  const hero = document.getElementById('hero');
  const navLinks = document.querySelectorAll<HTMLAnchorElement>('#global-nav a');

  if (!header || !hero) {
    return;
  }

  const update = () => {
    const heroBottom = hero.getBoundingClientRect().bottom;
    const shouldActivate = heroBottom <= 0;
    header.classList.toggle('bg-white/80', shouldActivate);
    header.classList.toggle('backdrop-blur-md', shouldActivate);
    header.classList.toggle('shadow-lg', shouldActivate);

    navLinks.forEach((link) => {
      if (link.classList.contains('bg-brand-navy')) {
        return;
      }

      if (shouldActivate) {
        link.classList.remove('text-white', 'hover:text-brand-navy');
        link.classList.add('text-brand-navy', 'hover:text-brand-navy/60');
      } else {
        link.classList.add('text-white', 'hover:text-brand-navy');
        link.classList.remove('text-brand-navy', 'hover:text-brand-navy/60');
      }
    });
  };

  window.addEventListener('scroll', update);
  window.addEventListener('resize', update);
  update();
};

const setupFadeInObserver = () => {
  const elements = document.querySelectorAll<HTMLElement>('.fade-in');
  if (!elements.length) {
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.1 }
  );

  elements.forEach((el) => observer.observe(el));
};

const setupBackToTop = () => {
  const button = document.getElementById('back-to-top');
  if (!button) {
    return;
  }

  const toggleVisibility = () => {
    if (window.scrollY > 400) {
      button.classList.add('visible');
    } else {
      button.classList.remove('visible');
    }
  };

  window.addEventListener('scroll', toggleVisibility);
  toggleVisibility();
};

const getHeaderOffset = (): number => {
  const header = document.querySelector<HTMLElement>('[data-site-header]');
  const adminBar = document.getElementById('wpadminbar');
  const headerHeight = header ? header.offsetHeight : 0;
  const adminHeight = adminBar ? adminBar.offsetHeight : 0;
  return headerHeight + adminHeight;
};

const setupSmoothScroll = () => {
  const links = document.querySelectorAll<HTMLAnchorElement>('a[href^="#"]');

  links.forEach((link) => {
    link.addEventListener('click', (event) => {
      const href = link.getAttribute('href');
      if (!href || href === '#') {
        return;
      }

      const targetId = href.replace('#', '');
      const target = targetId ? document.getElementById(targetId) : document.documentElement;
      if (!target) {
        return;
      }

      event.preventDefault();
      const targetRect = target.getBoundingClientRect();
      const offset = targetRect.top + window.scrollY - getHeaderOffset();
      window.scrollTo({ top: Math.max(offset, 0), behavior: 'smooth' });
    });
  });
};

const setupInterviewCarousel = () => {
  const container = document.querySelector('.interview-swiper');
  if (!container) {
    return;
  }

  // eslint-disable-next-line no-new
  new Swiper('.interview-swiper', {
    modules: [Navigation],
    slidesPerView: 1.1,
    spaceBetween: 16,
    navigation: {
      nextEl: '.interview-next',
      prevEl: '.interview-prev',
    },
    breakpoints: {
      640: { slidesPerView: 1.5 },
      768: { slidesPerView: 2, spaceBetween: 20 },
      1024: { slidesPerView: 2.5, spaceBetween: 24 },
    },
  });
};

const renderAnswers = (answers: string[]): string =>
  answers
    .map(
      (paragraph) => `
        <p class="leading-relaxed text-gray-700">${paragraph}</p>
      `
    )
    .join('');

const renderQA = (qa: InterviewQA[]): string =>
  qa
    .map(
      (item) => `
        <div class="rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div class="bg-brand-navy text-white px-5 py-3 flex items-center gap-4">
            <span class="text-xl font-black">${item.label ?? ''}</span>
            <p class="font-semibold leading-snug">${item.question}</p>
          </div>
          <div class="bg-white px-5 py-5 space-y-3">${renderAnswers(item.answer)}</div>
        </div>
      `
    )
    .join('');

const buildModalBody = (entry: InterviewEntry): string => {
  if (entry.qa && entry.qa.length > 0) {
    return renderQA(entry.qa);
  }

  if (entry.body) {
    return entry.body;
  }

  if (entry.excerpt) {
    return `<p class="leading-relaxed text-gray-700">${entry.excerpt}</p>`;
  }

  return '<p class="leading-relaxed text-gray-700">情報を読み込めませんでした。</p>';
};

const setupInterviewModal = () => {
  const modal = document.getElementById('interview-modal');
  const triggers = document.querySelectorAll<HTMLElement>('[data-interview-trigger]');

  if (!modal || !triggers.length) {
    return;
  }

  const titleEl = modal.querySelector<HTMLElement>('[data-interview-title]');
  const roleEl = modal.querySelector<HTMLElement>('[data-interview-role]');
  const bodyEl = modal.querySelector<HTMLElement>('[data-interview-body]');
  const closeTargets = modal.querySelectorAll<HTMLElement>('[data-modal-close]');
  const dataset = getInterviewData();

  const closeModal = () => {
    modal.classList.add('hidden');
    document.documentElement.classList.remove('overflow-hidden');
  };

  const openModal = (entry: InterviewEntry | undefined) => {
    if (!entry || !bodyEl) {
      return;
    }

    if (titleEl) {
      titleEl.textContent = entry.title;
    }

    if (roleEl) {
      roleEl.textContent = entry.role ?? '';
    }

    bodyEl.innerHTML = buildModalBody(entry);
    modal.classList.remove('hidden');
    document.documentElement.classList.add('overflow-hidden');
  };

  triggers.forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const targetId = trigger.getAttribute('data-interview-id');
      const match = dataset.find((entry) => `${entry.id}` === targetId);
      openModal(match);
    });
  });

  closeTargets.forEach((target) => target.addEventListener('click', closeModal));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !modal.classList.contains('hidden')) {
      closeModal();
    }
  });
};

const init = () => {
  setupMobileMenu();
  setupHeaderState();
  setupFadeInObserver();
  setupBackToTop();
  setupSmoothScroll();
  setupInterviewCarousel();
  setupInterviewModal();
};

document.addEventListener('DOMContentLoaded', init);
